import { onUnmounted, ref } from 'vue';

/**
 * Executes a callback using requestIdleCallback if available, otherwise executes immediately.
 * Automatically cancels the pending callback if the component is unmounted before execution.
 *
 * Use this composable when data is fetched for below-the-fold components that is not necessary for initial page load,
 * and should not block the rendering chain.
 *
 * @param callback - The function to execute during idle time
 * @returns A function to manually cancel the pending callback
 */
export function useIdleCallback(callback: () => void | Promise<void>): () => void {
	const handleId = ref<number | null>(null);
	const cancelled = ref(false);

	function cancel() {
		cancelled.value = true;
		if (handleId.value !== null && 'cancelIdleCallback' in window) {
			cancelIdleCallback(handleId.value);
			handleId.value = null;
		}
	}

	if ('requestIdleCallback' in window) {
		handleId.value = requestIdleCallback(() => {
			if (!cancelled.value) {
				callback();
			}
		});
	} else {
		Promise.resolve().then(() => {
			if (!cancelled.value) {
				callback();
			}
		});
	}

	onUnmounted(cancel);

	return cancel;
}
